import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { Workflow, WorkflowDetails, WorkflowStateDTO } from 'axiosApi/models';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import intl from 'components/i18n/ReactIntlWrapper';
import { StateMappingDTO } from 'api/WorkflowApiWrapper';

interface WorkflowActivateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** The inactive workflow the user wants to make active */
  workflowToActivate: Workflow;
  /** All workflows for the same module (to find the current active one) */
  moduleWorkflows: Workflow[];
}

const WorkflowActivateModal: React.FC<WorkflowActivateModalProps> = ({
  open,
  onClose,
  onSuccess,
  workflowToActivate,
  moduleWorkflows,
}) => {
  const api: Api = useApi();
  const toast = useToastMessageQueue();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>(null);

  // States of the workflow to activate (new workflow)
  const [newStates, setNewStates] = useState<WorkflowStateDTO[]>([]);
  // Non-empty state counts from the current active workflow
  const [nonEmptyCounts, setNonEmptyCounts] = useState<Record<number, number>>({});
  // State names from the current active workflow (for display)
  const [activeStates, setActiveStates] = useState<WorkflowStateDTO[]>([]);
  // User's mapping: fromStateId → toStateId
  const [mapping, setMapping] = useState<Record<number, number>>({});

  const activeWorkflow = moduleWorkflows.find((w) => w.active && w.id !== workflowToActivate.id);

  useEffect(() => {
    if (!open || !workflowToActivate) return;
    setMapping({});
    setErrors(null);
    loadData();
  }, [open, workflowToActivate]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load new workflow states
      const newWfResponse = await api.workflowApi.apiVversionWorkflowIdGet(
        workflowToActivate.id,
        '1',
        {}
      );
      const newWfDetails: WorkflowDetails = newWfResponse.data?.data;
      setNewStates(newWfDetails?.states || []);

      if (activeWorkflow) {
        // Load entity counts for the current active workflow
        const countsResponse = await api.workflowApiWrapper.getStateEntityCounts('1', activeWorkflow.id);
        const counts: Record<number, number> = countsResponse.data?.data || {};
        setNonEmptyCounts(counts);

        // Load active workflow details for state names
        const activeWfResponse = await api.workflowApi.apiVversionWorkflowIdGet(
          activeWorkflow.id,
          '1',
          {}
        );
        const activeWfDetails: WorkflowDetails = activeWfResponse.data?.data;
        setActiveStates(activeWfDetails?.states || []);
      } else {
        setNonEmptyCounts({});
        setActiveStates([]);
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    } finally {
      setLoading(false);
    }
  };

  const nonEmptyStateIds = Object.entries(nonEmptyCounts)
    .filter(([, count]) => count > 0)
    .map(([id]) => parseInt(id));

  const allMapped = nonEmptyStateIds.every((id) => mapping[id] != null);

  const handleActivate = async () => {
    if (!allMapped) return;

    setLoading(true);
    try {
      const stateMapping: StateMappingDTO[] = nonEmptyStateIds.map((fromStateId) => ({
        fromStateId,
        toStateId: mapping[fromStateId],
      }));

      await api.workflowApiWrapper.activateWorkflow('1', workflowToActivate.id, stateMapping);
      toast.success({ body: intl.get('workflow.activate.success') || 'Workflow activated successfully' });
      onSuccess();
      onClose();
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    } finally {
      setLoading(false);
    }
  };

  const getStateName = (state: WorkflowStateDTO) => {
    return (state.labelKey && intl.get(state.labelKey)) || state.name || `#${state.id}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {intl.get('workflow.activate') || 'Set Active'}: {workflowToActivate?.name}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {nonEmptyStateIds.length === 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {intl.get('workflow.activate.noMapping') ||
                  'No entity records exist in the current workflow states. You can activate this workflow directly.'}
              </Alert>
            ) : (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {intl.get('workflow.activate.mapping.description') ||
                    'The following states have existing records. Map each to a state in the new workflow.'}
                </Alert>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                  {nonEmptyStateIds.map((fromStateId) => {
                    const fromState = activeStates.find((s) => s.id === fromStateId);
                    const count = nonEmptyCounts[fromStateId];
                    return (
                      <Box
                        key={fromStateId}
                        display="flex"
                        alignItems="center"
                        gap={2}
                      >
                        <Box flex={1}>
                          <Typography variant="body2">
                            {fromState ? getStateName(fromState) : `State #${fromStateId}`}{' '}
                            <Typography component="span" variant="caption" color="text.secondary">
                              ({count} {intl.get('workflow.activate.mapping.records') || 'records'})
                            </Typography>
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          →
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>
                            {intl.get('workflow.activate.mapping.selectState') || 'New state'}
                          </InputLabel>
                          <Select
                            label={intl.get('workflow.activate.mapping.selectState') || 'New state'}
                            value={mapping[fromStateId] ?? ''}
                            onChange={(e) =>
                              setMapping((prev) => ({
                                ...prev,
                                [fromStateId]: e.target.value as number,
                              }))
                            }
                          >
                            {newStates.map((s) => (
                              <MenuItem key={s.id} value={s.id}>
                                {getStateName(s)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {intl.get('delete.modal.cancel.button') || 'Cancel'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleActivate}
          disabled={loading || !allMapped}
        >
          {intl.get('workflow.activate') || 'Set Active'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowActivateModal;
